import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import './ExportDialog.css';
import { useTranslation } from 'react-i18next';
import pdfIcon from '../../assets/images/Scene/pdf.png';
import excelIcon from '../../assets/images/Scene/excel.png';
import {
  exportPDF,
  exportScheduleCalendarPdf,
  exportScheduleDetailPdf,
  exportOneLineSchedulePdf,
  exportCastBreakdownPdf,
  SupportedLanguage,
  DEFAULT_PDF_SCENES_EXPORT_SORT,
  type PdfScenesExportSort,
  type PdfExportSortField,
} from './PDFExporter';
import DropdownMenu from '../DropdownMenu/DropdownMenu';
import { DropdownMenuItem } from '../DropdownMenu/DropdownMenu';
import { exportExcel } from './ExcelExporter';
import { buildExportDownloadFileName } from './exportFileName';
import DateSelect from '../DateSelect/DateSelect';
import { BreakdownInfo, BreakdownStatus, BreakdownDataStatus } from '../../pages/Home/types';
import { useDataCache } from '../../contexts/BreakdownDataCacheContext';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import {
  formatExportLanguage,
  parseExportLanguage,
} from '../../utils/userAppConfig';
import { ElementType } from '../../types/element.types';
import { PDF_SCENES_SETS_A4_MARGINS_MM } from './Templates/PDF-Breakdown-A4';
import { PDF_SCENES_SETS_A3_MARGINS_MM } from './Templates/PDF-Breakdown-A3';
import { PDF_SCHEDULE_CALENDAR_A3_MARGINS_MM } from './Templates/PDF-ScheduleCalendar';
import { PDF_SCENES_DETAIL_A4_MARGINS_MM } from './Templates/PDF-BreakdownDetail-A4';
import { PDF_SCHEDULE_DETAIL_A4_MARGINS_MM } from './Templates/PDF-ScheduleDetail-A4';
import { PDF_CAST_BREAKDOWN_A4_MARGINS_MM } from './Templates/PDF-CastBreakdown-A4';
import { EXCEL_SCENES_AND_SETS_MARGINS_INCH } from './Templates/Excel-Breakdown';
import { EXCEL_ELEMENT_SHOOTING_DATES_SUMMARY_MARGINS_INCH } from './Templates/Excel-ElementShootingDatesSummary';
import { EXCEL_ELEMENT_SHOOTING_DATES_DETAIL_MARGINS_INCH } from './Templates/Excel-ElementShootingDatesDetail';
import { EXCEL_ELEMENT_QTY_MARGINS_INCH } from './Templates/Excel-ElementQTY';
import {
  ensureBreakdownSchedulesListLoaded,
  ensureExportScheduleDataLoaded,
  exportOptionNeedsScheduleCalDateData,
  exportOptionNeedsScheduleSlotData
} from './ensureExportScheduleData';

/** 与 TabBar 中导出菜单位置计算的宽度保持一致 */
export const EXPORT_DIALOG_WIDTH_PX = 500;

/** 分场/分景导出排序：触发器与菜单同宽；宜窄以避免详情区折行 */
const EXPORT_SORT_FIELD_CONTROL_WIDTH_PX = 80;
const EXPORT_SCHEDULE_SELECTOR_WIDTH_PX = 210;

interface ExportDialogProps {
  visible: boolean;
  onClose: () => void;
  breakdownInfo?: any;
  scenes?: any[];
  breakdownId?: string;
  position?: {
    top?: number | string;
    left?: number | string;
    right?: number | string;
    bottom?: number | string;
  };
}

interface ExportType {
  id: string;
  name: string;
  type: 'pdf' | 'excel';
  icon: string;
  /** 是否在右侧显示 A4/A3 纸张选择 */
  supportsPageFormat?: boolean;
  /** 选中该项时的默认纸张（如制作日历默认 A3） */
  defaultPageFormat?: 'A4' | 'A3';
  /** 是否显示「是否导出无档期主演」 */
  showNoScheduleCastExport?: boolean;
  /** 是否固定 A4（显示 A4 但不允许修改） */
  fixedA4Only?: boolean;
}

type ExcelTemplateId =
  | 'breakdown-data-excel'
  | 'element-shooting-dates-summary-excel'
  | 'element-shooting-dates-detail-excel'
  | 'element-qty-excel';

type PdfTemplateId =
  | 'breakdown-a4'
  | 'sets-a4'
  | 'breakdown-a3'
  | 'sets-a3'
  | 'breakdowndetail-a4'
  | 'schedule-calendar-a3';

const MAX_CALENDAR_EXPORT_WEEKS = 52;

const EXPORT_TYPES_WITH_DATE_RANGE = new Set([
  'schedule-calendar-pdf',
  'schedule-detail-pdf',
  'one-line-schedule-pdf'
]);

/** 这些模板以拍摄计划为数据范围，文件名也应标识所选拍摄计划。 */
const EXPORT_TYPES_WITH_SCHEDULE_FILE_NAME = new Set([
  'schedule-calendar-pdf',
  'schedule-detail-pdf',
  'one-line-schedule-pdf',
  'element-shooting-dates-summary-excel',
  'element-shooting-dates-detail-excel',
  'element-qty-excel'
]);

function countWeeksInExportRange(startStr: string, endStr: string): number {
  const s = new Date(`${startStr}T12:00:00`);
  const e = new Date(`${endStr}T12:00:00`);
  const firstSun = new Date(s);
  firstSun.setDate(s.getDate() - s.getDay());
  let n = 0;
  const weekStart = new Date(firstSun);
  while (weekStart <= e) {
    const rowEnd = new Date(weekStart);
    rowEnd.setDate(weekStart.getDate() + 6);
    const rowStart = new Date(weekStart);
    if (rowEnd >= s && rowStart <= e) {
      n += 1;
    }
    weekStart.setDate(weekStart.getDate() + 7);
    if (n > 200) break;
  }
  return n;
}

function normalizeExportLanguage(lang: string | undefined): SupportedLanguage {
  const raw = lang || 'zh-CN';
  if (raw.startsWith('zh-HK')) return 'zh-HK';
  if (raw.startsWith('zh')) return 'zh-CN';
  return 'en';
}

function exportLanguageChar(code: SupportedLanguage): string {
  const charMap: Record<SupportedLanguage, string> = {
    'zh-CN': '简',
    'zh-HK': '繁',
    en: 'EN'
  };
  return charMap[code] ?? '简';
}

function exportLanguageName(code: SupportedLanguage): string {
  const nameMap: Record<SupportedLanguage, string> = {
    'zh-CN': '中文简体',
    'zh-HK': '中文繁体',
    en: 'English'
  };
  return nameMap[code] ?? '中文简体';
}

function formatExportMargins(
  t: (key: string, opts?: Record<string, unknown>) => string,
  language: SupportedLanguage,
  kind:
    | 'excel-breakdown-sets'
    | 'excel-element-shooting-dates-summary'
    | 'excel-element-shooting-dates-detail'
    | 'excel-element-qty'
    | 'pdf-a4-landscape-breakdown-sets'
    | 'pdf-a3-landscape-breakdown-sets'
    | 'pdf-a3-landscape-schedule-calendar'
    | 'pdf-a4-portrait-breakdown-detail'
    | 'pdf-a4-portrait-schedule-detail'
    | 'pdf-a4-portrait-cast-breakdown'
): string {
  if (
    kind === 'excel-breakdown-sets' ||
    kind === 'excel-element-shooting-dates-summary' ||
    kind === 'excel-element-shooting-dates-detail' ||
    kind === 'excel-element-qty'
  ) {
    const excelMarginsByKind = {
      'excel-breakdown-sets': EXCEL_SCENES_AND_SETS_MARGINS_INCH,
      'excel-element-shooting-dates-summary': EXCEL_ELEMENT_SHOOTING_DATES_SUMMARY_MARGINS_INCH,
      'excel-element-shooting-dates-detail': EXCEL_ELEMENT_SHOOTING_DATES_DETAIL_MARGINS_INCH,
      'excel-element-qty': EXCEL_ELEMENT_QTY_MARGINS_INCH
    } as const;
    const excelMargins = excelMarginsByKind[kind];
    const inchToMm = (inch: number) => Math.round(inch * 25.4);
    return t('export.marginsExcelPattern', {
      lng: language,
      lrtInch: excelMargins.left,
      lrtMm: inchToMm(excelMargins.left),
      bottomInch: excelMargins.bottom,
      bottomMm: inchToMm(excelMargins.bottom),
      hfInch: excelMargins.header,
      hfMm: inchToMm(excelMargins.header)
    }) as string;
  }

  const orientation =
    kind === 'pdf-a4-landscape-breakdown-sets' ||
    kind === 'pdf-a3-landscape-breakdown-sets' ||
    kind === 'pdf-a3-landscape-schedule-calendar'
      ? (t('export.marginsOrientationLandscape', { lng: language }) as string)
      : (t('export.marginsOrientationPortrait', { lng: language }) as string);

  const marginsByKind = {
    'pdf-a4-landscape-breakdown-sets': PDF_SCENES_SETS_A4_MARGINS_MM,
    'pdf-a3-landscape-breakdown-sets': PDF_SCENES_SETS_A3_MARGINS_MM,
    'pdf-a3-landscape-schedule-calendar': PDF_SCHEDULE_CALENDAR_A3_MARGINS_MM,
    'pdf-a4-portrait-breakdown-detail': PDF_SCENES_DETAIL_A4_MARGINS_MM,
    'pdf-a4-portrait-schedule-detail': PDF_SCHEDULE_DETAIL_A4_MARGINS_MM,
    'pdf-a4-portrait-cast-breakdown': PDF_CAST_BREAKDOWN_A4_MARGINS_MM
  } as const;

  const m = marginsByKind[kind];
  if (m.left === m.right && m.right === m.bottom) {
    return t('export.marginsPatternLrbTop', {
      lng: language,
      lrb: m.left,
      top: m.top,
      orientation
    }) as string;
  }

  if (m.left === m.right) {
    return t('export.marginsPatternLrTopBottom', {
      lng: language,
      lr: m.left,
      top: m.top,
      bottom: m.bottom,
      orientation
    }) as string;
  }

  return t('export.marginsPatternLrTopBottom', {
    lng: language,
    lr: `${m.left}/${m.right}`,
    top: m.top,
    bottom: m.bottom,
    orientation
  }) as string;
}

function parseYmdToDate(ymd: string | null): Date | null {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function getSoltDateBounds(slots: Array<{ slotDate?: string | null }>): { start: string; end: string } | null {
  const dates = slots
    .map((slot) => slot.slotDate?.split('T')[0] || '')
    .filter((s) => /^\d{4}-\d{2}-\d{2}$/.test(s))
    .sort();
  if (dates.length === 0) return null;
  return { start: dates[0], end: dates[dates.length - 1] };
}

function parseShootRangeIsoDate(s?: string | null): string | null {
  if (!s) return null;
  const p = s.split('T')[0];
  return /^\d{4}-\d{2}-\d{2}$/.test(p) ? p : null;
}

/** 导出文件名：选项段缺省时的 i18n 键（与 PDFExporter / Excel 一致） */
function exportFileNameOptionFallbackKey(typeId: string): string {
  switch (typeId) {
    case 'sets-table':
      return 'export.setsTable';
    case 'breakdowndetail-a4':
      return 'export.breakdownDetail';
    case 'schedule-calendar-pdf':
      return 'export.shootingCalendarTable';
    case 'cast-breakdown-pdf':
      return 'export.castSceneList';
    case 'breakdown-data-excel':
      return 'export.breakdownAndSetsTable';
    case 'element-shooting-dates-summary-excel':
      return 'export.elementsShootingDatesSummaryTable';
    case 'element-shooting-dates-detail-excel':
      return 'export.elementsShootingDatesDetailTable';
    case 'element-qty-excel':
      return 'export.actorsQTYTable';
    default:
      return 'export.breakdownTable';
  }
}

function exportOptionLabelKey(typeId: string): string {
  switch (typeId) {
    case 'sets-table':
      return 'export.setsTable';
    case 'breakdowndetail-a4':
      return 'export.breakdownDetail';
    case 'schedule-calendar-pdf':
      return 'export.shootingCalendarTable';
    case 'schedule-detail-pdf':
      return 'export.shootScheduleDetail';
    case 'one-line-schedule-pdf':
      return 'export.oneLineSchedule';
    case 'cast-breakdown-pdf':
      return 'export.castSceneList';
    case 'breakdown-data-excel':
      return 'export.breakdownAndSetsTable';
    case 'element-shooting-dates-summary-excel':
      return 'export.elementsShootingDatesSummaryTable';
    case 'element-shooting-dates-detail-excel':
      return 'export.elementsShootingDatesDetailTable';
    case 'element-qty-excel':
      return 'export.actorsQTYTable';
    default:
      return 'export.breakdownTable';
  }
}

/** 分场表：固定场号升序（无排序 UI）；分景表：默认按拍摄地点升序，排序项不含场号 */
function defaultPdfExportSortForTypeId(typeId: string | null): PdfScenesExportSort {
  if (typeId === 'sets-table') {
    return { field: 'sceneLocation', order: 'asc' };
  }
  return { ...DEFAULT_PDF_SCENES_EXPORT_SORT };
}

function resolvePdfTemplate(
  selectedId: string,
  pdfPageFormat: 'A4' | 'A3'
): PdfTemplateId | null {
  if (selectedId === 'breakdown-table') {
    return pdfPageFormat === 'A4' ? 'breakdown-a4' : 'breakdown-a3';
  }
  if (selectedId === 'sets-table') {
    return pdfPageFormat === 'A4' ? 'sets-a4' : 'sets-a3';
  }
  if (selectedId === 'breakdowndetail-a4') {
    return 'breakdowndetail-a4';
  }
  if (selectedId === 'schedule-calendar-pdf') {
    return 'schedule-calendar-a3';
  }
  return null;
}

const ExportDialog: React.FC<ExportDialogProps> = ({
  visible,
  onClose,
  breakdownInfo,
  scenes,
  breakdownId,
  position = {}
}) => {
  const { t, i18n } = useTranslation();
  const {
    getElementCache,
    getElementRelationCache,
    getSlotCache,
    getCalDateCache,
    getBreakdownCache,
    getScheduleCache,
    setSlotCache,
    setCalDateCache,
    setScheduleCache,
    subscribeScheduleCache,
    subscribeSlotCache
  } = useDataCache();
  const { getResolvedUserAppConfig, updateUserAppConfig, flushUserAppConfigSync } =
    useCurrentUser();
  const exportDialogWasVisibleRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // 导出进度状态
  const [exportProgress, setExportProgress] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [selectedExportTypeId, setSelectedExportTypeId] = useState<string | null>(null);
  /** 分场表 / 分景表 的纸张（制作日历固定 A3，不使用此项） */
  const [pdfPageFormat, setPdfPageFormat] = useState<'A4' | 'A3'>('A4');
  /** 制作日历：是否导出无档期主演 */
  const [includeNoScheduleCast, setIncludeNoScheduleCast] = useState(false);
  /** 制作日历：是否强制打印全部拍摄地点（不按任务条目上的地点可见性设置收敛） */
  const [forceShowAllLocations, setForceShowAllLocations] = useState(false);
  const [exportRangeStart, setExportRangeStart] = useState<string | null>(null);
  const [exportRangeEnd, setExportRangeEnd] = useState<string | null>(null);
  const [dateSelectOpen, setDateSelectOpen] = useState(false);
  const [dateSelectField, setDateSelectField] = useState<'start' | 'end'>('start');
  const [dateSelectPosition, setDateSelectPosition] = useState({ top: 0, left: 0 });
  const exportRangeStartBtnRef = useRef<HTMLButtonElement>(null);
  const exportRangeEndBtnRef = useRef<HTMLButtonElement>(null);
  const [exportDialogLanguage, setExportDialogLanguage] = useState<SupportedLanguage>(() =>
    normalizeExportLanguage(i18n.language)
  );
  const [exportDialogSecondaryLanguage, setExportDialogSecondaryLanguage] = useState<
    SupportedLanguage | null
  >(null);
  const [exportLangMenuVisible, setExportLangMenuVisible] = useState(false);
  const exportLangButtonRef = useRef<HTMLDivElement>(null);
  const [exportLangMenuPosition, setExportLangMenuPosition] = useState({ top: 0, left: 0 });
  const [exportSecondaryLangMenuVisible, setExportSecondaryLangMenuVisible] = useState(false);
  const exportSecondaryLangButtonRef = useRef<HTMLDivElement>(null);
  const [exportSecondaryLangMenuPosition, setExportSecondaryLangMenuPosition] = useState({
    top: 0,
    left: 0
  });
  const [pdfExportSort, setPdfExportSort] = useState<PdfScenesExportSort>(() =>
    defaultPdfExportSortForTypeId('breakdown-table')
  );
  const [sortFieldMenuVisible, setSortFieldMenuVisible] = useState(false);
  const sortFieldButtonRef = useRef<HTMLDivElement>(null);
  const [sortFieldMenuPosition, setSortFieldMenuPosition] = useState({ top: 0, left: 0 });
  const [exportScheduleId, setExportScheduleId] = useState<string | null>(null);
  const [exportScheduleMenuVisible, setExportScheduleMenuVisible] = useState(false);
  const exportScheduleButtonRef = useRef<HTMLDivElement>(null);
  const [exportScheduleMenuPosition, setExportScheduleMenuPosition] = useState({ top: 0, left: 0 });

  const [scheduleCacheTick, setScheduleCacheTick] = useState(0);
  useEffect(() => {
    if (!visible) return;
    if (!breakdownId) return;
    const unsubscribe = subscribeScheduleCache(breakdownId, () => {
      setScheduleCacheTick((x) => x + 1);
    });
    return unsubscribe;
  }, [visible, breakdownId, subscribeScheduleCache]);

  useEffect(() => {
    if (!visible || !breakdownId) return;
    void ensureBreakdownSchedulesListLoaded(breakdownId, getScheduleCache, setScheduleCache)
      .then(() => setScheduleCacheTick((x) => x + 1))
      .catch(() => {});
  }, [visible, breakdownId, getScheduleCache, setScheduleCache]);

  const exportTypes: ExportType[] = useMemo(
    () => [
      {
        id: 'breakdown-table',
        name: t('export.breakdownTable'),
        type: 'pdf' as const,
        icon: pdfIcon,
        supportsPageFormat: true
      },
      {
        id: 'sets-table',
        name: t('export.setsTable'),
        type: 'pdf' as const,
        icon: pdfIcon,
        supportsPageFormat: true
      },
      {
        id: 'breakdowndetail-a4',
        name: t('export.breakdownDetail'),
        type: 'pdf' as const,
        icon: pdfIcon
      },
      {
        id: 'schedule-calendar-pdf',
        name: t('export.shootingCalendarTable'),
        type: 'pdf' as const,
        icon: pdfIcon,
        showNoScheduleCastExport: true
      },
      {
        id: 'schedule-detail-pdf',
        name: t('export.shootScheduleDetail'),
        type: 'pdf' as const,
        icon: pdfIcon,
        supportsPageFormat: true,
        defaultPageFormat: 'A4',
        fixedA4Only: true
      },
      {
        id: 'one-line-schedule-pdf',
        name: t('export.oneLineSchedule'),
        type: 'pdf' as const,
        icon: pdfIcon,
        supportsPageFormat: true,
        defaultPageFormat: 'A4',
        fixedA4Only: true
      },
      {
        id: 'cast-breakdown-pdf',
        name: t('export.castSceneList'),
        type: 'pdf' as const,
        icon: pdfIcon,
        supportsPageFormat: true,
        defaultPageFormat: 'A4',
        fixedA4Only: true
      },
      {
        id: 'breakdown-data-excel',
        name: t('export.breakdownAndSetsTable'),
        type: 'excel' as const,
        icon: excelIcon
      },
      {
        id: 'element-shooting-dates-summary-excel',
        name: t('export.elementsShootingDatesSummaryTable'),
        type: 'excel' as const,
        icon: excelIcon
      },
      {
        id: 'element-shooting-dates-detail-excel',
        name: t('export.elementsShootingDatesDetailTable'),
        type: 'excel' as const,
        icon: excelIcon
      },
      {
        id: 'element-qty-excel',
        name: t('export.actorsQTYTable'),
        type: 'excel' as const,
        icon: excelIcon
      }
    ],
    [t]
  );

  const firstExportTypeId = exportTypes[0]?.id ?? null;

  useEffect(() => {
    if (visible && firstExportTypeId) {
      setSelectedExportTypeId(firstExportTypeId);
      setPdfPageFormat('A4');
      setIncludeNoScheduleCast(false);
      setForceShowAllLocations(false);
      setPdfExportSort(defaultPdfExportSortForTypeId(firstExportTypeId));
    }
  }, [visible, firstExportTypeId]);

  useEffect(() => {
    if (visible) {
      setExportDialogLanguage(normalizeExportLanguage(i18n.language));
      setExportDialogSecondaryLanguage(null);
    }
  }, [visible, i18n.language]);

  useEffect(() => {
    if (exportLangMenuVisible && exportLangButtonRef.current) {
      const rect = exportLangButtonRef.current.getBoundingClientRect();
      setExportLangMenuPosition({ top: rect.bottom + 4, left: rect.left });
    }
  }, [exportLangMenuVisible]);

  useEffect(() => {
    if (exportSecondaryLangMenuVisible && exportSecondaryLangButtonRef.current) {
      const rect = exportSecondaryLangButtonRef.current.getBoundingClientRect();
      setExportSecondaryLangMenuPosition({ top: rect.bottom + 4, left: rect.left });
    }
  }, [exportSecondaryLangMenuVisible]);

  useEffect(() => {
    if (sortFieldMenuVisible && sortFieldButtonRef.current) {
      const rect = sortFieldButtonRef.current.getBoundingClientRect();
      setSortFieldMenuPosition({ top: rect.bottom + 4, left: rect.left });
    }
  }, [sortFieldMenuVisible]);

  useEffect(() => {
    if (exportScheduleMenuVisible && exportScheduleButtonRef.current) {
      const rect = exportScheduleButtonRef.current.getBoundingClientRect();
      setExportScheduleMenuPosition({ top: rect.bottom + 4, left: rect.left });
    }
  }, [exportScheduleMenuVisible]);

  const exportLanguageOptions: DropdownMenuItem[] = useMemo(
    () => [
      { id: 'zh-CN', label: '中文简体' },
      { id: 'zh-HK', label: '中文繁体' },
      { id: 'en', label: 'English' }
    ],
    []
  );
  const secondaryLanguageOptions: DropdownMenuItem[] = useMemo(
    () => exportLanguageOptions.filter((item) => item.id !== exportDialogLanguage),
    [exportLanguageOptions, exportDialogLanguage]
  );

  useEffect(() => {
    if (exportDialogSecondaryLanguage === exportDialogLanguage) {
      setExportDialogSecondaryLanguage(null);
    }
  }, [exportDialogLanguage, exportDialogSecondaryLanguage]);

  useEffect(() => {
    if (!visible) return;
    const config = getResolvedUserAppConfig(i18n.language);
    const { primary, secondary } = parseExportLanguage(config.exportLanguage);
    setExportDialogLanguage(normalizeExportLanguage(primary));
    setExportDialogSecondaryLanguage(
      secondary ? normalizeExportLanguage(secondary as SupportedLanguage) : null,
    );
  }, [visible, getResolvedUserAppConfig, i18n.language]);

  useEffect(() => {
    if (exportDialogWasVisibleRef.current && !visible) {
      flushUserAppConfigSync();
    }
    exportDialogWasVisibleRef.current = visible;
  }, [visible, flushUserAppConfigSync]);

  /** 分景表 A3 模板仅支持按拍摄地点排序，与 PDF 模板一致 */
  const setsTableA3SortLocked = useMemo(
    () => selectedExportTypeId === 'sets-table' && pdfPageFormat === 'A3',
    [selectedExportTypeId, pdfPageFormat]
  );

  const pdfSortFieldMenuItems: DropdownMenuItem[] = useMemo(() => {
    const all: DropdownMenuItem[] = [
      { id: 'sceneCode', label: t('export.sortFieldSceneCode') },
      { id: 'sceneSet', label: t('export.sortFieldSceneSet') },
      { id: 'sceneLocation', label: t('export.sortFieldSceneLocation') },
      { id: 'scenePeriod', label: t('export.sortFieldScenePeriod') }
    ];
    if (selectedExportTypeId === 'sets-table') {
      return all.filter((item) => item.id !== 'sceneCode');
    }
    return all;
  }, [t, selectedExportTypeId]);

  const pdfSortFieldLabel = useMemo(() => {
    const map: Record<PdfExportSortField, string> = {
      sceneCode: t('export.sortFieldSceneCode'),
      sceneSet: t('export.sortFieldSceneSet'),
      sceneLocation: t('export.sortFieldSceneLocation'),
      scenePeriod: t('export.sortFieldScenePeriod')
    };
    return map[pdfExportSort.field];
  }, [pdfExportSort.field, t]);

  /** 导出拍摄计划选择：默认当前拍摄计划；若无则由缓存回退最新一条 */
  const effectiveScheduleId = useMemo(() => {
    if (!visible || !breakdownId) return null;
    void scheduleCacheTick;
    const cache = getScheduleCache(breakdownId);
    const schedulesRows = cache?.schedules || [];
    if (schedulesRows.length === 0) return null;
    const matchesCachedScheduleId = (id?: string | null) => !!id && schedulesRows.some((p) => p.scheduleID === id);
    if (matchesCachedScheduleId(cache?.currentScheduleId)) return cache?.currentScheduleId ?? null;
    return [...schedulesRows].sort((a, b) => {
      const timeA = a.scheduleUpdateTime ? new Date(a.scheduleUpdateTime).getTime() : 0;
      const timeB = b.scheduleUpdateTime ? new Date(b.scheduleUpdateTime).getTime() : 0;
      return timeB - timeA;
    })[0]?.scheduleID ?? null;
  }, [visible, breakdownId, getScheduleCache, scheduleCacheTick]);

  const availableSchedules = useMemo(() => {
    if (!visible || !breakdownId) return [];
    void scheduleCacheTick;
    const cache = getScheduleCache(breakdownId);
    return cache?.schedules || [];
  }, [visible, breakdownId, getScheduleCache, scheduleCacheTick]);

  const exportScheduleMenuItems: DropdownMenuItem[] = useMemo(
    () => {
      if (availableSchedules.length === 0) {
        return [{ id: '__no-schedule__', label: t('export.noScheduleAvailable') }];
      }
      return availableSchedules.map((sch) => ({
        id: sch.scheduleID,
        label: (sch.scheduleName || '').trim() || `${t('export.schedule')} ${sch.scheduleID.slice(0, 8)}`
      }));
    },
    [availableSchedules, t]
  );

  const activeExportScheduleId = exportScheduleId || effectiveScheduleId;

  const exportScheduleDropdownLabel = useMemo(() => {
    if (!activeExportScheduleId) return t('export.selectSchedule');
    const active = availableSchedules.find((p) => p.scheduleID === activeExportScheduleId);
    if (!active) return t('export.selectSchedule');
    return (active.scheduleName || '').trim() || `${t('export.schedule')} ${active.scheduleID.slice(0, 8)}`;
  }, [activeExportScheduleId, availableSchedules, t]);

  const activeExportScheduleName = useMemo(() => {
    if (!activeExportScheduleId) return null;
    const active = availableSchedules.find((p) => p.scheduleID === activeExportScheduleId);
    return (active?.scheduleName || '').trim() || null;
  }, [activeExportScheduleId, availableSchedules]);

  useEffect(() => {
    if (!visible) return;
    if (!effectiveScheduleId) {
      setExportScheduleId(null);
      return;
    }
    if (!exportScheduleId) {
      setExportScheduleId(effectiveScheduleId);
      return;
    }
    if (!availableSchedules.some((p) => p.scheduleID === exportScheduleId)) {
      setExportScheduleId(effectiveScheduleId);
    }
  }, [visible, effectiveScheduleId, exportScheduleId, availableSchedules]);

  useEffect(() => {
    if (!visible) return;
    if (!activeExportScheduleId) {
      setExportRangeStart(null);
      setExportRangeEnd(null);
      return;
    }
    void scheduleCacheTick;
    const cache = breakdownId ? getScheduleCache(breakdownId) : null;
    const scheduleRow = cache?.schedules?.find((p) => p.scheduleID === activeExportScheduleId);
    // 日历导出默认范围与拍摄计划页右侧开机/杀青一致；若缓存 SHOOT 有更窄边界则优先 SHOOT。
    const slotCache = getSlotCache(activeExportScheduleId);
    const slotBounds = getSoltDateBounds(slotCache?.slots || []);
    let start = slotBounds?.start ?? parseShootRangeIsoDate(scheduleRow?.scheduleStartDate ?? null);
    let end = slotBounds?.end ?? parseShootRangeIsoDate(scheduleRow?.scheduleEndDate ?? null);
    if (!start) start = parseShootRangeIsoDate(scheduleRow?.scheduleStartDate ?? null);
    if (!end) end = parseShootRangeIsoDate(scheduleRow?.scheduleEndDate ?? null);
    setExportRangeStart(start);
    setExportRangeEnd(end);
  }, [visible, activeExportScheduleId, breakdownId, getScheduleCache, getSlotCache, scheduleCacheTick]);

  /** 行程详情 PDF：用拍摄计划开机/杀青日与导出区间相交（模板侧再过滤有 SHOOT 的拍摄日） */
  const scheduleDetailExportDateBounds = useMemo(() => {
    if (!visible || !activeExportScheduleId || !breakdownId) return null;
    void scheduleCacheTick;
    const cache = getScheduleCache(breakdownId);
    const scheduleRow = cache?.schedules?.find((p) => p.scheduleID === activeExportScheduleId);
    let start = parseShootRangeIsoDate(scheduleRow?.scheduleStartDate ?? null);
    let end = parseShootRangeIsoDate(scheduleRow?.scheduleEndDate ?? null);
    if (!start || !end) {
      const slotCache = getSlotCache(activeExportScheduleId);
      const slotBounds = getSoltDateBounds(slotCache?.slots || []);
      if (!start) start = slotBounds?.start ?? null;
      if (!end) end = slotBounds?.end ?? null;
    }
    if (!start || !end || start > end) return null;
    return { start, end };
  }, [visible, activeExportScheduleId, breakdownId, getScheduleCache, getSlotCache, scheduleCacheTick]);

  useEffect(() => {
    if (!visible || !activeExportScheduleId) return;
    const unsubscribe = subscribeSlotCache(activeExportScheduleId, () => {
      setScheduleCacheTick((x) => x + 1);
    });
    return unsubscribe;
  }, [visible, activeExportScheduleId, subscribeSlotCache]);

  useEffect(() => {
    if (!visible || !activeExportScheduleId || !breakdownId) return;
    if (!exportOptionNeedsScheduleSlotData(selectedExportTypeId)) return;
    void ensureExportScheduleDataLoaded({
      scheduleId: activeExportScheduleId,
      breakdownId,
      requireCalDates: exportOptionNeedsScheduleCalDateData(selectedExportTypeId),
      getSlotCache,
      getCalDateCache,
      getScheduleCache,
      setSlotCache,
      setCalDateCache,
      setScheduleCache
    }).catch(() => {
      /* 预取失败不打断对话框，导出时再提示 */
    });
  }, [
    visible,
    activeExportScheduleId,
    breakdownId,
    selectedExportTypeId,
    getSlotCache,
    getCalDateCache,
    getScheduleCache,
    setSlotCache,
    setCalDateCache,
    setScheduleCache
  ]);

  const ensureScheduleDataForExport = useCallback(
    async (optionId: string): Promise<boolean> => {
      if (!exportOptionNeedsScheduleSlotData(optionId)) return true;
      if (!activeExportScheduleId) {
        setExportError(t('export.exportNeedScheduleId'));
        setTimeout(() => setExportError(null), 4000);
        return false;
      }
      try {
        await ensureExportScheduleDataLoaded({
          scheduleId: activeExportScheduleId,
          breakdownId,
          requireCalDates: exportOptionNeedsScheduleCalDateData(optionId),
          getSlotCache,
          getCalDateCache,
          getScheduleCache,
          setSlotCache,
          setCalDateCache,
          setScheduleCache
        });
        return true;
      } catch {
        setExportError(t('export.exportNeedScheduleId'));
        setTimeout(() => setExportError(null), 4000);
        return false;
      }
    },
    [
      activeExportScheduleId,
      breakdownId,
      getSlotCache,
      getCalDateCache,
      getScheduleCache,
      setSlotCache,
      setCalDateCache,
      setScheduleCache,
      t
    ]
  );

  useEffect(() => {
    if (!visible) return;
    if (!setsTableA3SortLocked) return;
    setPdfExportSort((prev) => {
      if (prev.field === 'sceneLocation' && prev.order === 'asc') return prev;
      return { field: 'sceneLocation', order: 'asc' };
    });
    setSortFieldMenuVisible(false);
  }, [visible, setsTableA3SortLocked]);

  const applyExportTypeSelection = useCallback((item: ExportType) => {
    setSelectedExportTypeId(item.id);
    setExportScheduleMenuVisible(false);
    if (item.supportsPageFormat) {
      setPdfPageFormat(item.defaultPageFormat ?? 'A4');
    }
    if (item.id === 'breakdown-table' || item.id === 'sets-table') {
      setPdfExportSort(defaultPdfExportSortForTypeId(item.id));
    }
  }, []);

  // 点击外部区域关闭
  useEffect(() => {
    if (!visible) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dateSelectOpen ||
        exportLangMenuVisible ||
        exportSecondaryLangMenuVisible ||
        sortFieldMenuVisible ||
        exportScheduleMenuVisible
      )
        return;
      const target = e.target as HTMLElement;

      if (containerRef.current && containerRef.current.contains(target)) {
        return;
      }

      onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [
    visible,
    onClose,
    dateSelectOpen,
    exportLangMenuVisible,
    exportSecondaryLangMenuVisible,
    sortFieldMenuVisible,
    exportScheduleMenuVisible
  ]);

  const selectedType = useMemo(
    () => exportTypes.find((o) => o.id === selectedExportTypeId) ?? null,
    [exportTypes, selectedExportTypeId]
  );

  const resolvedPdfTemplate = useMemo((): PdfTemplateId | null => {
    if (!selectedExportTypeId || selectedType?.type !== 'pdf') {
      return null;
    }
    return resolvePdfTemplate(selectedExportTypeId, pdfPageFormat);
  }, [selectedExportTypeId, selectedType?.type, pdfPageFormat]);

  const detailInfoRows = useMemo(() => {
    const defaultInfo: BreakdownInfo = {
      breakdownID: '',
      breakdownName: t('export.untitledBreakdown'),
      breakdownStatus: BreakdownStatus.PREPARING,
      breakdownDataStatus: BreakdownDataStatus.NORMAL
    };
    const info = breakdownInfo || defaultInfo;
    const filmName = typeof window !== 'undefined' ? localStorage.getItem('selectedFilmName') : null;
    const lang = exportDialogLanguage;

    if (!selectedType) {
      return [];
    }

    if (selectedType.type === 'excel') {
      const excelMarginsKind =
        selectedType.id === 'element-shooting-dates-summary-excel'
          ? 'excel-element-shooting-dates-summary'
          : selectedType.id === 'element-shooting-dates-detail-excel'
            ? 'excel-element-shooting-dates-detail'
            : selectedType.id === 'element-qty-excel'
              ? 'excel-element-qty'
              : 'excel-breakdown-sets';
      const pageLayoutSummary =
        selectedType.id === 'element-shooting-dates-detail-excel'
          ? t('export.excelPrintLayoutShootingDatesDetail', { lng: lang })
          : t('export.excelPrintLayoutSummary', { lng: lang });
      const tableTypeLabel = t(exportOptionLabelKey(selectedType.id), { lng: lang }) as string;
      const fileNameSubject = EXPORT_TYPES_WITH_SCHEDULE_FILE_NAME.has(selectedType.id)
        ? activeExportScheduleName
        : info.breakdownName;
      const fileName = buildExportDownloadFileName({
        lng: lang,
        filmName,
        optionDisplayName: tableTypeLabel,
        optionFallbackI18nKey: exportFileNameOptionFallbackKey(selectedType.id),
        breakdownDisplayName: fileNameSubject,
        breakdownFallbackI18nKey: EXPORT_TYPES_WITH_SCHEDULE_FILE_NAME.has(selectedType.id)
          ? 'export.schedule'
          : undefined,
        extension: 'xlsx'
      });
      return [
        { label: t('export.detailLabelFileName'), value: fileName },
        { label: t('export.detailLabelFileType'), value: t('export.fileTypeExcel') },
        { label: t('export.detailLabelPageSize'), value: t('export.pageSizeA4Landscape') },
        { label: t('export.detailLabelMargins'), value: formatExportMargins(t, lang, excelMarginsKind) },
        { label: t('export.detailLabelPageLayout'), value: pageLayoutSummary as string }
      ];
    }

    if (selectedType.id === 'schedule-detail-pdf' || selectedType.id === 'one-line-schedule-pdf') {
      const optionFallbackKey =
        selectedType.id === 'one-line-schedule-pdf'
          ? 'export.oneLineSchedule'
          : 'export.shootScheduleDetail';
      const optionName = t(exportOptionLabelKey(selectedType.id), { lng: lang }) as string;
      const fileName = buildExportDownloadFileName({
        lng: lang,
        filmName,
        optionDisplayName: optionName,
        optionFallbackI18nKey: optionFallbackKey,
        breakdownDisplayName: activeExportScheduleName,
        breakdownFallbackI18nKey: 'export.schedule',
        extension: 'pdf'
      });
      const rows = [
        { label: t('export.detailLabelFileName'), value: fileName },
        { label: t('export.detailLabelFileType'), value: t('export.fileTypePdf') },
        { label: t('export.detailLabelPageSize'), value: t('export.pageSizeA4Portrait') },
        { label: t('export.detailLabelMargins'), value: formatExportMargins(t, lang, 'pdf-a4-portrait-schedule-detail') }
      ];
      if (exportRangeStart && exportRangeEnd) {
        rows.splice(4, 0, {
          label: t('export.exportRangeStart'),
          value: exportRangeStart
        }, {
          label: t('export.exportRangeEnd'),
          value: exportRangeEnd
        });
      }
      return rows;
    }

    if (selectedType.id === 'cast-breakdown-pdf') {
      const optionName = t(exportOptionLabelKey(selectedType.id), { lng: lang }) as string;
      const fileName = buildExportDownloadFileName({
        lng: lang,
        filmName,
        optionDisplayName: optionName,
        optionFallbackI18nKey: 'export.castSceneList',
        breakdownDisplayName: info.breakdownName,
        breakdownFallbackI18nKey: 'export.untitledBreakdown',
        stripBreakdownSegmentWhitespace: true,
        extension: 'pdf'
      });
      return [
        { label: t('export.detailLabelFileName'), value: fileName },
        { label: t('export.detailLabelFileType'), value: t('export.fileTypePdf') },
        { label: t('export.detailLabelPageSize'), value: t('export.pageSizeA4Portrait') },
        { label: t('export.detailLabelMargins'), value: formatExportMargins(t, lang, 'pdf-a4-portrait-cast-breakdown') }
      ];
    }

    const template = resolvedPdfTemplate;
    if (!template) {
      return [];
    }

    const fileName = buildExportDownloadFileName({
      lng: lang,
      filmName,
      optionDisplayName: t(exportOptionLabelKey(selectedType.id), { lng: lang }) as string,
      optionFallbackI18nKey: exportFileNameOptionFallbackKey(selectedType.id),
      breakdownDisplayName: selectedType.id === 'schedule-calendar-pdf'
        ? activeExportScheduleName
        : info.breakdownName,
      breakdownFallbackI18nKey: selectedType.id === 'schedule-calendar-pdf'
        ? 'export.schedule'
        : undefined,
      extension: 'pdf'
    });

    let pageSize = '';
    if (template === 'breakdowndetail-a4') {
      pageSize = t('export.pageSizeA4Portrait');
    } else if (template === 'schedule-calendar-a3') {
      pageSize = t('export.pageSizeA3Landscape');
    } else if (template === 'breakdown-a4' || template === 'sets-a4') {
      pageSize = t('export.pageSizeA4Landscape');
    } else {
      pageSize = t('export.pageSizeA3Landscape');
    }

    let margins = '';
    if (template === 'breakdowndetail-a4') {
      margins = formatExportMargins(t, lang, 'pdf-a4-portrait-breakdown-detail');
    } else if (template === 'schedule-calendar-a3') {
      margins = formatExportMargins(t, lang, 'pdf-a3-landscape-schedule-calendar');
    } else if (template === 'breakdown-a3' || template === 'sets-a3') {
      margins = formatExportMargins(t, lang, 'pdf-a3-landscape-breakdown-sets');
    } else {
      margins = formatExportMargins(t, lang, 'pdf-a4-landscape-breakdown-sets');
    }

    const baseRows: { label: string; value: string }[] = [
      { label: t('export.detailLabelFileName'), value: fileName },
      { label: t('export.detailLabelFileType'), value: t('export.fileTypePdf') },
      { label: t('export.detailLabelPageSize'), value: pageSize },
      { label: t('export.detailLabelMargins'), value: margins }
    ];
    if (template === 'schedule-calendar-a3') {
      baseRows.push({
        label: t('export.exportNoScheduleCastLabel'),
        value: includeNoScheduleCast ? t('home.yes') : t('home.no')
      });
      if (exportRangeStart && exportRangeEnd) {
        baseRows.push(
          { label: t('export.exportRangeStart'), value: exportRangeStart },
          { label: t('export.exportRangeEnd'), value: exportRangeEnd }
        );
      }
    }
    return baseRows;
  }, [
    selectedType,
    breakdownInfo,
    resolvedPdfTemplate,
    t,
    exportDialogLanguage,
    includeNoScheduleCast,
    exportRangeStart,
    exportRangeEnd,
    activeExportScheduleName
  ]);

  const openExportDateSelect = useCallback(
    (field: 'start' | 'end') => {
      const btn = field === 'start' ? exportRangeStartBtnRef.current : exportRangeEndBtnRef.current;
      if (btn) {
        const rect = btn.getBoundingClientRect();
        setDateSelectPosition({ top: rect.bottom + 4, left: rect.left });
      } else {
        setDateSelectPosition({ top: 80, left: 80 });
      }
      setDateSelectField(field);
      setDateSelectOpen(true);
    },
    []
  );

  const handleExport = async () => {
    const option = exportTypes.find((o) => o.id === selectedExportTypeId);
    if (!option) {
      return;
    }

    const needsSceneRows =
      option.type === 'excel' ||
      (option.type === 'pdf' &&
        option.id !== 'schedule-calendar-pdf' &&
        option.id !== 'schedule-detail-pdf' &&
        option.id !== 'one-line-schedule-pdf' &&
        option.id !== 'cast-breakdown-pdf');
    if (needsSceneRows && (!scenes || scenes.length === 0)) {
      console.error(t('export.noExportData'));
      return;
    }

    console.log('导出选项:', option, '场景数据:', scenes);

    const exportLanguage = exportDialogLanguage;
    const exportSecondaryLanguage = exportDialogSecondaryLanguage || undefined;
    const optionNameByExportLanguage = t(exportOptionLabelKey(option.id), {
      lng: exportLanguage
    }) as string;

    if (option.id === 'schedule-detail-pdf' || option.id === 'one-line-schedule-pdf') {
      if (!(await ensureScheduleDataForExport(option.id))) {
        return;
      }
      if (!exportRangeStart || !exportRangeEnd || exportRangeStart > exportRangeEnd) {
        setExportError(t('export.exportInvalidDateRange'));
        setTimeout(() => setExportError(null), 4000);
        return;
      }
      const pt = getSlotCache(activeExportScheduleId!);
      const pdt = getCalDateCache(activeExportScheduleId!);
      if (!pt) {
        setExportError(t('export.exportNeedScheduleId'));
        setTimeout(() => setExportError(null), 4000);
        return;
      }
      try {
        setIsExporting(true);
        setExportProgress(0);
        setExportError(null);
        await new Promise((resolve) => setTimeout(resolve, 0));
        const defaultBreakdownInfo: BreakdownInfo = {
          breakdownID: '',
          breakdownName: t('export.untitledBreakdown'),
          breakdownStatus: BreakdownStatus.PREPARING,
          breakdownDataStatus: BreakdownDataStatus.NORMAL
        };
        const exportParams = {
          language: exportLanguage,
          secondaryLanguage: exportSecondaryLanguage,
          scenes: scenes || [],
          breakdownInfo: breakdownInfo || defaultBreakdownInfo,
          scheduleInfo: pt.scheduleInfo ?? null,
          scheduleSolts: pt.slots || [],
          calendarDays: pdt?.calDates || [],
          exportRange: {
            start: exportRangeStart,
            end: exportRangeEnd
          },
          optionName: optionNameByExportLanguage,
          onProgress: (p: number) => setExportProgress(p)
        };
        if (option.id === 'schedule-detail-pdf') {
          await exportScheduleDetailPdf({
            ...exportParams,
            breakdownId: breakdownId,
            getElementCache,
            getElementRelationCache
          });
        } else {
          await exportOneLineSchedulePdf({
            ...exportParams,
            breakdownId: breakdownId,
            getElementCache,
            getElementRelationCache
          });
        }
        setTimeout(() => {
          setIsExporting(false);
          setExportProgress(0);
          onClose();
        }, 10000);
      } catch (error) {
        setIsExporting(false);
        setExportProgress(0);
        const errorMessage = error instanceof Error ? error.message : t('export.exportFailed');
        setExportError(errorMessage);
        setTimeout(() => setExportError(null), 3000);
      }
      return;
    }

    if (option.id === 'cast-breakdown-pdf') {
      if (!breakdownId) {
        setExportError(t('export.exportNeedBreakdownId'));
        setTimeout(() => setExportError(null), 4000);
        return;
      }
      if (!scenes || scenes.length === 0) {
        setExportError(t('export.noExportData'));
        setTimeout(() => setExportError(null), 4000);
        return;
      }
      const items = getElementCache(breakdownId)?.elements ?? [];
      if (!items.some((i) => i.elementType === ElementType.CAST)) {
        setExportError(t('export.noCastToExport'));
        setTimeout(() => setExportError(null), 4000);
        return;
      }
      try {
        setIsExporting(true);
        setExportProgress(0);
        setExportError(null);
        await new Promise((resolve) => setTimeout(resolve, 0));
        const defaultBreakdownInfo: BreakdownInfo = {
          breakdownID: '',
          breakdownName: t('export.untitledBreakdown'),
          breakdownStatus: BreakdownStatus.PREPARING,
          breakdownDataStatus: BreakdownDataStatus.NORMAL
        };
        await exportCastBreakdownPdf({
          language: exportLanguage,
          secondaryLanguage: exportSecondaryLanguage,
          breakdownInfo: breakdownInfo || defaultBreakdownInfo,
          breakdownId: breakdownId,
          optionName: optionNameByExportLanguage,
          getElementCache,
          getElementRelationCache,
          getBreakdownCache,
          onProgress: (p) => setExportProgress(p)
        });
        setTimeout(() => {
          setIsExporting(false);
          setExportProgress(0);
          onClose();
        }, 10000);
      } catch (error) {
        setIsExporting(false);
        setExportProgress(0);
        const errorMessage = error instanceof Error ? error.message : t('export.exportFailed');
        setExportError(errorMessage);
        setTimeout(() => setExportError(null), 3000);
      }
      return;
    }

    if (option.id === 'schedule-calendar-pdf') {
      if (!(await ensureScheduleDataForExport(option.id))) {
        return;
      }
      if (!exportRangeStart || !exportRangeEnd || exportRangeStart > exportRangeEnd) {
        setExportError(t('export.exportInvalidDateRange'));
        setTimeout(() => setExportError(null), 4000);
        return;
      }
      const weekN = countWeeksInExportRange(exportRangeStart, exportRangeEnd);
      if (weekN > MAX_CALENDAR_EXPORT_WEEKS) {
        setExportError(t('export.exportRangeTooManyWeeks'));
        setTimeout(() => setExportError(null), 4000);
        return;
      }
      const pt = getSlotCache(activeExportScheduleId!);
      const pdt = getCalDateCache(activeExportScheduleId!);
      if (!pt) {
        setExportError(t('export.exportNeedScheduleId'));
        setTimeout(() => setExportError(null), 4000);
        return;
      }
      try {
        setIsExporting(true);
        setExportProgress(0);
        setExportError(null);
        await new Promise((resolve) => setTimeout(resolve, 0));
        const defaultBreakdownInfo: BreakdownInfo = {
          breakdownID: '',
          breakdownName: t('export.untitledBreakdown'),
          breakdownStatus: BreakdownStatus.PREPARING,
          breakdownDataStatus: BreakdownDataStatus.NORMAL
        };
        const planCache = breakdownId ? getScheduleCache(breakdownId) : null;
        const planRow =
          planCache?.schedules?.find((row) => row.scheduleID === activeExportScheduleId) ??
          (planCache?.currentScheduleInfo?.scheduleID === activeExportScheduleId
            ? planCache.currentScheduleInfo
            : null);
        const scheduleInfo = pt.scheduleInfo
          ? {
              ...pt.scheduleInfo,
              scheduleStartDate: planRow?.scheduleStartDate || pt.scheduleInfo.scheduleStartDate,
            }
          : planRow ?? null;
        await exportScheduleCalendarPdf({
          language: exportLanguage,
          secondaryLanguage: exportSecondaryLanguage,
          scenes: scenes || [],
          breakdownInfo: breakdownInfo || defaultBreakdownInfo,
          breakdownId: breakdownId,
          scheduleInfo,
          scheduleSolts: pt.slots || [],
          calendarDays: pdt?.calDates || [],
          exportRange: { start: exportRangeStart, end: exportRangeEnd },
          includeNoScheduleCast,
          forceShowAllLocations,
          optionName: optionNameByExportLanguage,
          getElementCache,
          getElementRelationCache,
          onProgress: (p) => setExportProgress(p)
        });
        setTimeout(() => {
          setIsExporting(false);
          setExportProgress(0);
          onClose();
        }, 10000);
      } catch (error) {
        setIsExporting(false);
        setExportProgress(0);
        const errorMessage = error instanceof Error ? error.message : t('export.exportFailed');
        setExportError(errorMessage);
        setTimeout(() => setExportError(null), 3000);
      }
      return;
    }

    if (option.type === 'pdf') {
      const template = resolvePdfTemplate(option.id, pdfPageFormat);
      if (!template) {
        return;
      }

      try {
        setIsExporting(true);
        setExportProgress(0);

        await new Promise(resolve => setTimeout(resolve, 0));

        const defaultBreakdownInfo: BreakdownInfo = {
          breakdownID: '',
          breakdownName: t('export.untitledBreakdown'),
          breakdownStatus: BreakdownStatus.PREPARING,
          breakdownDataStatus: BreakdownDataStatus.NORMAL
        };

        setExportError(null);

        const optionName = optionNameByExportLanguage;

        await exportPDF(
          scenes!,
          breakdownInfo || defaultBreakdownInfo,
          template as 'breakdown-a4' | 'sets-a4' | 'breakdown-a3' | 'sets-a3' | 'breakdowndetail-a4',
          exportLanguage,
          exportSecondaryLanguage,
          optionName,
          breakdownId,
          getElementCache,
          getElementRelationCache,
          (progress: number) => {
            setExportProgress(progress);
          },
          pdfExportSort
        );

        setTimeout(() => {
          setIsExporting(false);
          setExportProgress(0);
          onClose();
        }, 10000);
      } catch (error) {
        setIsExporting(false);
        setExportProgress(0);
        const errorMessage = error instanceof Error ? error.message : t('export.exportFailed');
        setExportError(errorMessage);
        setTimeout(() => {
          setExportError(null);
        }, 3000);
      }
    } else if (option.type === 'excel') {
      if (!(await ensureScheduleDataForExport(option.id))) {
        return;
      }
      try {
        setIsExporting(true);
        setExportProgress(0);
        setExportError(null);

        await new Promise(resolve => setTimeout(resolve, 0));

        const defaultBreakdownInfo: BreakdownInfo = {
          breakdownID: '',
          breakdownName: t('export.untitledBreakdown'),
          breakdownStatus: BreakdownStatus.PREPARING,
          breakdownDataStatus: BreakdownDataStatus.NORMAL
        };

        const excelTemplateId = option.id as ExcelTemplateId;
        await exportExcel(
          scenes!,
          breakdownInfo || defaultBreakdownInfo,
          exportLanguage,
          exportSecondaryLanguage,
          excelTemplateId,
          breakdownId,
          activeExportScheduleId,
          scheduleDetailExportDateBounds || undefined,
          getScheduleCache,
          getSlotCache,
          getElementCache,
          getElementRelationCache,
          (progress: number) => {
            setExportProgress(progress);
          }
        );

        setTimeout(() => {
          setIsExporting(false);
          setExportProgress(0);
          onClose();
        }, 10000);
      } catch (error) {
        setIsExporting(false);
        setExportProgress(0);
        const errorMessage = error instanceof Error ? error.message : t('export.exportFailed');
        setExportError(errorMessage);
        setTimeout(() => {
          setExportError(null);
        }, 3000);
      }
    }
  };

  if (!visible) return null;

  const style: React.CSSProperties = {
    ...position,
    width: `${EXPORT_DIALOG_WIDTH_PX}px`,
    position: 'fixed'
  };

  const needsScenesData =
    selectedType?.type === 'excel' ||
    (selectedType?.type === 'pdf' &&
      selectedType.id !== 'schedule-calendar-pdf' &&
      selectedType.id !== 'schedule-detail-pdf' &&
      selectedType.id !== 'one-line-schedule-pdf' &&
      selectedType.id !== 'cast-breakdown-pdf');

  const calendarExportBlocked =
    selectedType?.id === 'schedule-calendar-pdf' &&
    (!activeExportScheduleId ||
      !exportRangeStart ||
      !exportRangeEnd ||
      exportRangeStart > exportRangeEnd ||
      countWeeksInExportRange(exportRangeStart, exportRangeEnd) > MAX_CALENDAR_EXPORT_WEEKS);
  const scheduleDetailExportBlocked =
    (selectedType?.id === 'schedule-detail-pdf' || selectedType?.id === 'one-line-schedule-pdf') &&
    (!activeExportScheduleId ||
      !exportRangeStart ||
      !exportRangeEnd ||
      exportRangeStart > exportRangeEnd);

  const castBreakdownPdfExportBlocked =
    selectedType?.id === 'cast-breakdown-pdf' &&
    (!breakdownId ||
      !scenes?.length ||
      !(getElementCache(breakdownId)?.elements ?? []).some((i) => i.elementType === ElementType.CAST));

  const confirmDisabled =
    (needsScenesData && !scenes?.length) ||
    selectedExportTypeId == null ||
    isExporting ||
    calendarExportBlocked ||
    scheduleDetailExportBlocked ||
    castBreakdownPdfExportBlocked;

  const showPaperFormat = Boolean(selectedType?.supportsPageFormat);
  const showBreakdownSetsSortOptions =
    showPaperFormat && selectedExportTypeId === 'sets-table';
  const showNoScheduleCastOption = Boolean(selectedType?.showNoScheduleCastExport);
  const showScheduleDateRange = Boolean(
    selectedType?.id && EXPORT_TYPES_WITH_DATE_RANGE.has(selectedType.id)
  );
  const showPrintLocationOption = selectedType?.id === 'schedule-calendar-pdf';
  const showShootScheduleSelector =
    selectedType?.id === 'schedule-calendar-pdf' ||
    selectedType?.id === 'schedule-detail-pdf' ||
    selectedType?.id === 'one-line-schedule-pdf' ||
    selectedType?.id === 'element-shooting-dates-summary-excel' ||
    selectedType?.id === 'element-shooting-dates-detail-excel' ||
    selectedType?.id === 'element-qty-excel';

  const dateSelectBaseDate =
    parseYmdToDate(dateSelectField === 'start' ? exportRangeStart : exportRangeEnd) || new Date();

  return (
    <div className="export-dropdown-menu" ref={containerRef} style={style}>
      <DateSelect
        visible={dateSelectOpen}
        onClose={() => setDateSelectOpen(false)}
        position={dateSelectPosition}
        selectedYear={dateSelectBaseDate.getFullYear()}
        selectedMonth={dateSelectBaseDate.getMonth() + 1}
        onSelectMonth={() => {}}
        enableDateSelection
        selectedDate={dateSelectBaseDate}
        onSelectDate={(d) => {
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          const ymd = `${y}-${m}-${day}`;
          if (dateSelectField === 'start') {
            setExportRangeStart(ymd);
          } else {
            setExportRangeEnd(ymd);
          }
          setDateSelectOpen(false);
        }}
      />
      <div className="export-dropdown-dialog">
        <div className="export-dropdown-header-wrapper">
          <div className="export-dropdown-header">
            <h3>{t('export.selectExportFormat')}</h3>
            <div className="export-dropdown-header-actions">
              {exportError && (
                <span className="export-error-message">{exportError}</span>
              )}
              <span className="export-header-lang-label">{t('export.primaryLanguageLabel')}</span>
              <div
                ref={exportLangButtonRef}
                className="info-value-with-icon export-header-lang-select"
                onClick={() => setExportLangMenuVisible(!exportLangMenuVisible)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setExportLangMenuVisible((v) => !v);
                  }
                }}
              >
                <span className="language-icon-char">
                  {exportLanguageChar(exportDialogLanguage)}
                </span>
                <span>{exportLanguageName(exportDialogLanguage)}</span>
              </div>
              <DropdownMenu
                items={exportLanguageOptions}
                visible={exportLangMenuVisible}
                onClose={() => setExportLangMenuVisible(false)}
                onSelect={(item) => {
                  const newPrimary = item.id as SupportedLanguage;
                  setExportDialogLanguage(newPrimary);
                  updateUserAppConfig({
                    exportLanguage: formatExportLanguage(
                      newPrimary,
                      exportDialogSecondaryLanguage,
                    ),
                  });
                  setExportLangMenuVisible(false);
                }}
                position={exportLangMenuPosition}
                useFixedPosition
                selectedValue={exportDialogLanguage}
                width={exportLangButtonRef.current?.offsetWidth}
              />
              <span className="export-header-lang-separator">{t('export.secondaryLanguageLabel')}</span>
              <div
                ref={exportSecondaryLangButtonRef}
                className="info-value-with-icon export-header-lang-select"
                onClick={() => setExportSecondaryLangMenuVisible(!exportSecondaryLangMenuVisible)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setExportSecondaryLangMenuVisible((v) => !v);
                  }
                }}
              >
                <span>
                  {exportDialogSecondaryLanguage
                    ? exportLanguageName(exportDialogSecondaryLanguage)
                    : '—'}
                </span>
              </div>
              <DropdownMenu
                items={secondaryLanguageOptions}
                visible={exportSecondaryLangMenuVisible}
                onClose={() => setExportSecondaryLangMenuVisible(false)}
                onSelect={(item) => {
                  const newSecondary = item.id as SupportedLanguage;
                  setExportDialogSecondaryLanguage(newSecondary);
                  updateUserAppConfig({
                    exportLanguage: formatExportLanguage(exportDialogLanguage, newSecondary),
                  });
                  setExportSecondaryLangMenuVisible(false);
                }}
                position={exportSecondaryLangMenuPosition}
                useFixedPosition
                selectedValue={exportDialogSecondaryLanguage ?? undefined}
                width={exportSecondaryLangButtonRef.current?.offsetWidth}
              />
            </div>
          </div>
          {isExporting && (
            <div className="export-progress-container">
              <div
                className="export-progress-bar"
                style={{ width: `${Math.max(exportProgress, 1)}%` }}
              />
            </div>
          )}
        </div>

        <div className="export-dialog-body">
          <div className="export-type">
            {exportTypes.map((item) => (
              <div
                key={item.id}
                className={`export-dropdown-item export-type-item ${
                  selectedExportTypeId === item.id ? 'is-selected' : ''
                }`}
                onClick={() => applyExportTypeSelection(item)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    applyExportTypeSelection(item);
                  }
                }}
              >
                <div className="export-dropdown-item-left">
                  <img
                    src={item.icon}
                    alt={item.type}
                    className="export-dropdown-item-icon"
                  />
                  <span className="export-dropdown-item-name">{item.name}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="export-details">
            <div className="export-details-options">
              {showShootScheduleSelector && (
                <div className="export-sort-row info-item">
                  <span className="info-label export-sort-method-label">
                    {t('export.schedule')}
                  </span>
                  <div className="export-sort-controls">
                    <div
                      ref={exportScheduleButtonRef}
                      className="export-sort-dropdown-trigger export-schedule-dropdown-trigger"
                      style={{ width: EXPORT_SCHEDULE_SELECTOR_WIDTH_PX }}
                      role="button"
                      tabIndex={0}
                      onClick={() => setExportScheduleMenuVisible((v) => !v)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setExportScheduleMenuVisible((v) => !v);
                        }
                      }}
                    >
                      <span>{exportScheduleDropdownLabel}</span>
                    </div>
                    <DropdownMenu
                      items={exportScheduleMenuItems}
                      visible={exportScheduleMenuVisible}
                      onClose={() => setExportScheduleMenuVisible(false)}
                      onSelect={(item) => {
                        const selectedScheduleId = String(item.id);
                        if (selectedScheduleId === '__no-schedule__') {
                          setExportScheduleMenuVisible(false);
                          return;
                        }
                        setExportScheduleId(selectedScheduleId);
                        setExportScheduleMenuVisible(false);
                      }}
                      position={exportScheduleMenuPosition}
                      useFixedPosition
                      selectedValue={activeExportScheduleId || undefined}
                      width={EXPORT_SCHEDULE_SELECTOR_WIDTH_PX}
                      className="export-schedule-dropdown"
                      dataDropdownId="export-schedule-select"
                    />
                  </div>
                </div>
              )}
              {showScheduleDateRange && (
                <div className="export-date-range-row">
                  <span className="export-date-range-title">{t('export.exportDateRangeTitle')}</span>
                  <button
                    type="button"
                    ref={exportRangeStartBtnRef}
                    className="export-date-pick-btn"
                    onClick={() => openExportDateSelect('start')}
                  >
                    {exportRangeStart ?? t('export.pickDate')}
                  </button>
                  <span className="export-date-range-sep" aria-hidden>
                    -
                  </span>
                  <button
                    type="button"
                    ref={exportRangeEndBtnRef}
                    className="export-date-pick-btn"
                    onClick={() => openExportDateSelect('end')}
                  >
                    {exportRangeEnd ?? t('export.pickDate')}
                  </button>
                </div>
              )}
              {showPaperFormat && (
                <div className="info-item">
                  <span className="info-label">{t('export.paperSize')}</span>
                  <div className="info-value-radio-group">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="export-paper-format"
                        checked={pdfPageFormat === 'A4'}
                        onChange={() => {
                          if (!selectedType?.fixedA4Only) setPdfPageFormat('A4');
                        }}
                        disabled={Boolean(selectedType?.fixedA4Only)}
                      />
                      <span>A4</span>
                    </label>
                    {!selectedType?.fixedA4Only && (
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="export-paper-format"
                          checked={pdfPageFormat === 'A3'}
                          onChange={() => {
                            if (!selectedType?.fixedA4Only) setPdfPageFormat('A3');
                          }}
                          disabled={Boolean(selectedType?.fixedA4Only)}
                        />
                        <span>A3</span>
                      </label>
                    )}
                  </div>
                </div>
              )}
              {showBreakdownSetsSortOptions && (
                <div
                  className={`export-sort-row info-item${setsTableA3SortLocked ? ' is-sort-locked' : ''}`}
                >
                  <span className="info-label export-sort-method-label">{t('export.sortMethod')}</span>
                  <div className="export-sort-controls">
                    <span className="export-sort-by-prefix">{t('export.sortByPrefix')}</span>
                    {setsTableA3SortLocked ? (
                      <span
                        className="export-sort-field-readonly"
                        style={{ width: EXPORT_SORT_FIELD_CONTROL_WIDTH_PX }}
                      >
                        {t('export.sortFieldSceneLocation')}
                      </span>
                    ) : (
                      <>
                        <div
                          ref={sortFieldButtonRef}
                          className="export-sort-dropdown-trigger"
                          style={{ width: EXPORT_SORT_FIELD_CONTROL_WIDTH_PX }}
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            setSortFieldMenuVisible((v) => !v);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setSortFieldMenuVisible((v) => !v);
                            }
                          }}
                        >
                          <span>{pdfSortFieldLabel}</span>
                        </div>
                        <DropdownMenu
                          items={pdfSortFieldMenuItems}
                          visible={sortFieldMenuVisible}
                          onClose={() => setSortFieldMenuVisible(false)}
                          onSelect={(item) => {
                            setPdfExportSort((prev) => ({
                              ...prev,
                              field: item.id as PdfExportSortField,
                              order: 'asc'
                            }));
                            setSortFieldMenuVisible(false);
                          }}
                          position={sortFieldMenuPosition}
                          useFixedPosition
                          selectedValue={pdfExportSort.field}
                          width={EXPORT_SORT_FIELD_CONTROL_WIDTH_PX}
                          className="export-sort-field-dropdown"
                          dataDropdownId="export-sort-field"
                        />
                      </>
                    )}
                  </div>
                </div>
              )}
              {showNoScheduleCastOption && (
                <div className="info-item">
                  <span className="info-label export-details-option-label-wrap">
                    {t('export.exportNoScheduleCastLabel')}
                  </span>
                  <div className="info-value-radio-group">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="export-no-schedule-cast"
                        checked={includeNoScheduleCast}
                        onChange={() => setIncludeNoScheduleCast(true)}
                      />
                      <span>{t('home.yes')}</span>
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="export-no-schedule-cast"
                        checked={!includeNoScheduleCast}
                        onChange={() => setIncludeNoScheduleCast(false)}
                      />
                      <span>{t('home.no')}</span>
                    </label>
                  </div>
                </div>
              )}
              {showPrintLocationOption && (
                <div className="info-item">
                  <span className="info-label export-details-option-label-wrap">
                    {t('export.printShootingLocation')}
                  </span>
                  <div className="info-value-radio-group">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="export-print-location-mode"
                        checked={forceShowAllLocations}
                        onChange={() => setForceShowAllLocations(true)}
                      />
                      <span>{t('export.printShootingLocationAll')}</span>
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="export-print-location-mode"
                        checked={!forceShowAllLocations}
                        onChange={() => setForceShowAllLocations(false)}
                      />
                      <span>{t('export.printShootingLocationGroup')}</span>
                    </label>
                  </div>
                </div>
              )}
            </div>
            <div className="export-details-info">
              {detailInfoRows.map((row) => (
                <div className="info-item" key={row.label}>
                  <span className="info-label">{row.label}</span>
                  <span className="info-value-text">{row.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="export-bottom">
          <button
            type="button"
            className="export-confirm-btn"
            disabled={confirmDisabled}
            onClick={() => void handleExport()}
          >
            {t('export.confirmExport')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportDialog;
